import React, { useEffect, useState } from "react";
import { IconCheck, IconException, IconClose, IconArrowTop, IconArrowBottom, Pill, LineChart, htmlLegendPlugin } from "@geotab/zenith";
import { InfoModal } from "../infoModal/infoModal";
import { splitDateAndTime, toStringFormat } from "../../utils/date";
import { IDriverSafetyDataStore } from "../../../store/types";
import { Chart, ChartType, CoreInteractionOptions, Plugin, TooltipModel, TooltipOptions } from "chart.js/auto";
import { CHART_BLUE, CHART_GREEN, greenGradient, hideAfterMouseUp, verticalLine } from "./chartUtils";
import { useTranslationContext } from "../../../../../ui/framework/hooks/TranslationContext";
import { useUserServiceContext } from "../../../../../ui/framework/hooks/UserServiceContext";
import User from "../../../../../model/User";
import { getDriverSafetyModalClassNames } from "../../utils/driverSafetyModalClassNames";

const YOU_LABEL = "You";
const TOP_PERFORMER_LABEL = "Top Performers";
const LOW_RISK = "Low Risk";
const MODERATE_RISK = "Moderate Risk";
const HIGH_RISK = "High Risk";
const LOW_RISK_LOWERCASE = "low risk";
const MODERATE_RISK_LOWERCASE = "moderate risk";
const HIGH_RISK_LOWERCASE = "high risk";
const NO_DATA_AVAILABLE = "No Data Available";
const RISK_MESSAGE_METRIC_TEMPLATE = "{xx.x%} collision risk in the next 100,000 km";
const RISK_MESSAGE_IMPERIAL_TEMPLATE = "{xx.x%} collision risk in the next 100,000 mi";
const INFO_MODAL_TITLE = "Collision Risk";
const COLLISION_RISK_MODAL_TEMPLATE = "You have a {xx.x%} chance of collision, a {risk level} compared to other similar drivers.*";
const LAST_UPDATED = "Last updated:";
const ESTIMATED_VALUE = "*Estimated value that takes your risk factors into account.";
const LAST_7_DAYS = "Last 7 days";
const LAST_30_DAYS = "Last 30 days";
const LAST_3_MONTHS = "Last 3 months";
const LAST_6_MONTHS = "Last 6 months";
const LAST_12_MONTHS = "Last 12 months";
const FILTER_LABELS = [LAST_7_DAYS, LAST_30_DAYS, LAST_3_MONTHS, LAST_6_MONTHS, LAST_12_MONTHS];

// The time from the API is in UTC without a time component (It's always 00:00:00). To preserve the date without resorting to a
// big refactor (since this only affets a chart) we add 12 hours to the date to display the correct date.
const ROUNDUP_TIME = 12;

enum RiskLevels {
    High = "High",
    Low = "Low",
    Moderate = "Moderate"
}

interface ICollisionRiskSection {
    driverSafetyData: IDriverSafetyDataStore[];
}

const toRoundedPercentage = (unitInterval: number, decimalPlaces = 2): number => {
    const multiplier = Math.pow(10, decimalPlaces);
    return Math.round(unitInterval * 100 * multiplier) / multiplier;
};

export const calculateRiskLabel = (data: IDriverSafetyDataStore | null, isMetric: boolean): string | undefined => {
    if (!data) {
        return undefined;
    }
    let probabilityComparison: number | undefined;
    if (isMetric) {
        if (data.crashProbabilityBenchmarkKm === undefined || data.crashProbabilityKm === undefined) {
            return undefined;
        }
        probabilityComparison = toRoundedPercentage(data.crashProbabilityKm, 1) - toRoundedPercentage(data.crashProbabilityBenchmarkKm, 1);
    } else {
        if (data.crashProbabilityBenchmarkMile === undefined || data.crashProbabilityMile === undefined) {
            return undefined;
        }
        probabilityComparison = toRoundedPercentage(data.crashProbabilityMile, 1) - toRoundedPercentage(data.crashProbabilityBenchmarkMile, 1);
    }
    return probabilityComparison > 1 ? RiskLevels.High : probabilityComparison < -1 ? RiskLevels.Low : RiskLevels.Moderate;
};

//Known issue with ChartJS where on mobile the tooltip will not disappear when pressing outside the canvas
//https://github.com/chartjs/Chart.js/issues/7738
export const handleTouchEnd = () => {
    const tooltipElement = document.getElementById("collision-risk-tooltip") as HTMLDivElement;
    if (tooltipElement) {
        tooltipElement.style.display = "none";
    }
    (document.querySelector(".collision-risk-section") as HTMLDivElement)?.classList.remove("collision-risk-section__is-background");
};

export const CollisionRiskSection: React.FC<ICollisionRiskSection> = ({ driverSafetyData }) => {
    const { translate } = useTranslationContext();
    const { userService } = useUserServiceContext();
    let currentUser: IUser = new User(userService.getCurrentUser());
    const language = currentUser.language;

    const localeOptions = { style: "percent" as const, maximumFractionDigits: 1, minimumFractionDigits: 1 };
    const [currentFilter, setCurrentFilter] = useState<number>(0);
    const chartPlugins: Plugin<"line">[] = [htmlLegendPlugin(), verticalLine, hideAfterMouseUp, greenGradient] as Plugin<"line">[];
    const isMetric = currentUser.isMetric || false;

    // MYG-92067 The number of extra days to add to each filter range
    // This is due to a decision taken by the design team to add an additonal day to the date for visual purposes
    // Only apply the extra day if we have at least 2 data points, matching the extrapolateDataPoint function behavior
    const extraDaysForFiltering = driverSafetyData.length >= 2 ? 1 : 0;

    const filteredDriverSafetyData = React.useMemo(() => {
        const today = new Date();
        let startDate: Date;
        switch (currentFilter) {
            case 1: {
                // Last 30 days + extra days
                startDate = new Date(today);
                startDate.setDate(today.getDate() - 30 - extraDaysForFiltering);
                break;
            }
            case 2: {
                // Last 3 months + extra days
                startDate = new Date(today);
                startDate.setMonth(today.getMonth() - 3);
                startDate.setDate(startDate.getDate() - extraDaysForFiltering);
                break;
            }
            case 3: {
                // Last 6 months + extra days
                startDate = new Date(today);
                startDate.setMonth(today.getMonth() - 6);
                startDate.setDate(startDate.getDate() - extraDaysForFiltering);
                break;
            }
            case 4: {
                // Last 12 months + extra days
                startDate = new Date(today);
                startDate.setFullYear(today.getFullYear() - 1);
                startDate.setDate(startDate.getDate() - extraDaysForFiltering);
                break;
            }
            default: {
                // Last 7 days + extra days
                startDate = new Date(today);
                startDate.setDate(today.getDate() - 7 - extraDaysForFiltering);
            }
        }
        return driverSafetyData.filter(datum => new Date(datum.observationDate) >= startDate);
    }, [currentFilter, driverSafetyData]);

    const mostRecentInsight = filteredDriverSafetyData[filteredDriverSafetyData.length - 1];
    const probability = isMetric ? mostRecentInsight?.crashProbabilityKm : mostRecentInsight?.crashProbabilityMile;

    useEffect(() => {
        window.addEventListener("touchend", handleTouchEnd);

        return () => {
            window.removeEventListener("touchend", handleTouchEnd);
        };
    }, []);

    const externalTooltipHandler = (context: { chart: Chart; tooltip: TooltipModel<ChartType> }) => {
        const { chart, tooltip } = context;
        const tooltipElement = document.getElementById("collision-risk-tooltip") as HTMLDivElement;

        if (tooltip.body) {
            const titleLines = tooltip.title || [];
            const titleDiv = document.createElement("div");
            titleLines.forEach(title => {
                const titleContainer = document.createElement("b");
                titleContainer.classList.add("data-04-mobile-drive", "collision-risk-tooltip__bold");
                const localizedTitle = toStringFormat(title, splitDateAndTime(currentUser.dateFormat).date);

                titleContainer.textContent = localizedTitle;
                titleDiv.appendChild(titleContainer);
            });

            const dataPointsDiv = document.createElement("div");
            dataPointsDiv.classList.add("body-04-mobile-drive", "collision-risk-tooltip__data-points");
            tooltip.dataPoints.forEach((dataPoint, i) => {
                const datasetDiv = document.createElement("div");
                datasetDiv.classList.add("collision-risk-tooltip__data-line");
                const colors = tooltip.labelColors[i];

                const dataPointCircle = document.createElement("span");
                dataPointCircle.classList.add("collision-risk-tooltip__circle");
                dataPointCircle.style.background = colors.backgroundColor as string;
                dataPointCircle.style.borderColor = colors.borderColor as string;
                datasetDiv.appendChild(dataPointCircle);

                const textContainer = document.createElement("div");
                textContainer.classList.add("collision-risk-tooltip__data-text");
                const labelText = document.createElement("span");
                labelText.innerText = dataPoint.dataset.label as string;
                labelText.classList.add("collision-risk-tooltip__label");
                const percentageText = `${ (dataPoint.raw as number).toLocaleString(language, localeOptions) }`;
                const boldText = document.createElement("b");
                boldText.classList.add("collision-risk-tooltip__bold");
                boldText.textContent = percentageText;
                textContainer.appendChild(labelText);
                textContainer.appendChild(boldText);
                datasetDiv.appendChild(textContainer);

                dataPointsDiv.appendChild(datasetDiv);
            });

            const tooltipRoot = document.getElementById("collision-risk-tooltip");

            // Cleanup
            while (tooltipRoot?.firstChild) {
                tooltipRoot.firstChild.remove();
            }

            tooltipRoot?.appendChild(titleDiv);
            tooltipRoot?.appendChild(dataPointsDiv);
        }

        const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

        // Check bounds of tooltip element and adjust if off screen
        tooltipElement.style.removeProperty("left");
        tooltipElement.style.removeProperty("right");
        let styleLeft = positionX + tooltip.caretX;
        //90px is leftiest part of window, 200px is the width of the tooltip
        if (styleLeft < 120) {
            tooltipElement.style.left = "120px";
        } else if (styleLeft + 200 - 120 > window.innerWidth) {
            tooltipElement.style.right = "-109px";
        } else {
            tooltipElement.style.left = styleLeft + "px";
        }
        let styleTop = positionY + tooltip.caretY;
        tooltipElement.style.top = styleTop + "px";
        tooltipElement.style.display = "flex";
        (document.querySelector(".collision-risk-section") as HTMLDivElement).classList.add("collision-risk-section__is-background");
    };

    const chartOptions = {
        scales: {
            x: {
                grid: {
                    display: false,
                    drawBorder: false
                },
                ticks: {
                    display: false
                }
            },
            y: {
                grid: {
                    display: false,
                    drawBorder: false
                },
                ticks: { display: false }
            }
        },
        interaction: {
            mode: "index",
            intersect: false
        } as CoreInteractionOptions,
        events: ["touchstart", "touchmove", "touchend"] as (keyof HTMLElementEventMap)[],
        plugins: {
            tooltip: {
                enabled: false,
                external: externalTooltipHandler
            } as TooltipOptions,
            htmlLegend: {
                containerID: "collision-risk-legend"
            }
        }
    };

    const lineChartData = React.useMemo(() => {
        const youLabel = translate(YOU_LABEL);
        const topPerformerLabel = translate(TOP_PERFORMER_LABEL);
        const graphData = filteredDriverSafetyData;
        const chartData = {
            datasets: [
                {
                    label: youLabel,
                    data: graphData.map(datum => {
                        if (!datum.crashProbabilityKm || !datum.crashProbabilityMile) {
                            return null;
                        }
                        return isMetric ? datum.crashProbabilityKm : datum.crashProbabilityMile;
                    }),
                    backgroundColor: CHART_BLUE
                },
                {
                    label: topPerformerLabel,
                    data: graphData.map(datum => {
                        if (!datum.crashProbabilityBestInClassKm || !datum.crashProbabilityBestInClassMile) {
                            return null;
                        }
                        return isMetric ? datum.crashProbabilityBestInClassKm : datum.crashProbabilityBestInClassMile;
                    }),
                    borderDash: [5, 5],
                    borderColor: CHART_GREEN,
                    backgroundColor: CHART_GREEN
                }
            ],
            labels: graphData.map(datum => {
                const date = new Date(datum.observationDate);
                date.setHours(date.getHours() + ROUNDUP_TIME);
                return date;
            })
        };

        return chartData;
    }, [isMetric, filteredDriverSafetyData]);

    const getRiskModalText = React.useMemo(() => {
        if (!probability) {
            return null;
        }
        const percentageText = probability.toLocaleString(language, localeOptions);
        const riskLevel = calculateRiskLabel(mostRecentInsight, isMetric) ?? "na";
        const translatedRiskLevel = translate(
            riskLevel === RiskLevels.High ? HIGH_RISK_LOWERCASE : riskLevel === RiskLevels.Low ? LOW_RISK_LOWERCASE : riskLevel === RiskLevels.Moderate ? MODERATE_RISK_LOWERCASE : NO_DATA_AVAILABLE
        );
        const riskMessage = translate(COLLISION_RISK_MODAL_TEMPLATE);
        const riskParts = riskMessage.split("{xx.x%}");
        const riskPartsFinal = riskParts[1].split("{risk level}");
        const estimatedValueText = translate(ESTIMATED_VALUE);

        const lastUpdatedText = `${ translate(LAST_UPDATED) } ${ toStringFormat(mostRecentInsight?.observationDate, splitDateAndTime(currentUser.dateFormat).date) }`;

        const { driverSafetyModalTextFactorClassName, driverSafetyModalTextMessageClassName } = getDriverSafetyModalClassNames();

        return (
            <div className="driver-safety-modal-text">
                <div className={driverSafetyModalTextMessageClassName}>
                    {riskParts[0]}
                    <b>{percentageText}</b>
                    {riskPartsFinal[0]}
                    <b>{translatedRiskLevel}</b>
                    {riskPartsFinal[1]}
                </div>
                <div className={driverSafetyModalTextFactorClassName}>
                    <span>{estimatedValueText}</span>
                </div>
                <div className={driverSafetyModalTextFactorClassName}>
                    <span>{lastUpdatedText}</span>
                </div>
            </div>
        );
    }, [currentUser.dateFormat, language, mostRecentInsight, probability]);

    const getRiskLabel = React.useMemo(() => {
        const crashProbabilityRiskLabel = (mostRecentInsight && calculateRiskLabel(mostRecentInsight, isMetric)) ?? "na";
        const riskLabel = translate(
            crashProbabilityRiskLabel === RiskLevels.High
                ? HIGH_RISK
                : crashProbabilityRiskLabel === RiskLevels.Low
                  ? LOW_RISK
                  : crashProbabilityRiskLabel === RiskLevels.Moderate
                    ? MODERATE_RISK
                    : NO_DATA_AVAILABLE
        );

        const RiskIcon = crashProbabilityRiskLabel === RiskLevels.High ? IconException : crashProbabilityRiskLabel === RiskLevels.Low ? IconCheck : IconClose;
        const iconContainerClass = `collision-risk-section__icon-container collision-risk-section__icon-container--${ crashProbabilityRiskLabel.toLowerCase() }`;
        const dataTestId = `collision-risk-icon-${ crashProbabilityRiskLabel.toLowerCase() }`;

        return (
            <>
                {crashProbabilityRiskLabel !== RiskLevels.Moderate ? (
                    <div className={iconContainerClass} data-testid={dataTestId}>
                        <RiskIcon size="huge" className="collision-risk-section__icon" />
                    </div>
                ) : null}
                <div className="collision-risk-section__label-container">
                    <p className="collision-risk-section__label">{riskLabel}</p>
                    <InfoModal title={translate(INFO_MODAL_TITLE)} content={getRiskModalText} />
                </div>
            </>
        );
    }, [getRiskModalText, mostRecentInsight]);

    const getRiskText = React.useMemo(() => {
        if (!probability) {
            return null;
        }
        const percentageText = probability.toLocaleString(language, localeOptions);
        const percentageSpan = <span className="collision-risk-section__percentage">{percentageText}</span>;
        const riskMessage = translate(isMetric ? RISK_MESSAGE_METRIC_TEMPLATE : RISK_MESSAGE_IMPERIAL_TEMPLATE);
        const riskParts = riskMessage.split("{xx.x%}");
        const riskText = (
            <>
                {riskParts[0]}
                {percentageSpan}
                {riskParts[1]}
            </>
        );
        return (
            <p className="collision-risk-section__text" data-testid="collision-risk-text">
                {riskText}
            </p>
        );
    }, [probability, language, isMetric]);

    const getRiskTrend = React.useMemo(() => {
        const hasMultipleInsights = filteredDriverSafetyData.length > 1;
        const firstInsight = filteredDriverSafetyData[0];
        const initialCrashProbability = isMetric ? firstInsight?.crashProbabilityKm : firstInsight?.crashProbabilityMile;
        const latestCrashProbability = isMetric ? mostRecentInsight?.crashProbabilityKm : mostRecentInsight?.crashProbabilityMile;
        if (!(initialCrashProbability && latestCrashProbability && hasMultipleInsights)) {
            return null;
        }
        const trendPercentageChange = latestCrashProbability - initialCrashProbability;
        const trendDirection = trendPercentageChange > 0 ? "error" : trendPercentageChange < 0 ? "success" : "default";
        const trendIconComponent = trendPercentageChange > 0 ? IconArrowTop : trendPercentageChange < 0 ? IconArrowBottom : undefined;
        const formattedTrendPercentage = Math.abs(trendPercentageChange).toLocaleString(language, localeOptions);

        return (
            <Pill className="collision-risk-section__trend" type={trendDirection} icon={trendIconComponent} size="fullWidth">
                <span data-testid="collision-risk-trend">{`${ formattedTrendPercentage } ${ translate(FILTER_LABELS[currentFilter]) }`}</span>
            </Pill>
        );
    }, [filteredDriverSafetyData, isMetric, language, mostRecentInsight]);

    return (
        <>
            <div className="collision-risk-section">
                {getRiskLabel}
                {getRiskText}
                {getRiskTrend}
                <div className="collision-risk-section__graph">
                    {filteredDriverSafetyData.length > 0 && (
                        <>
                            <div className="collision-risk-section__graph-canvas">
                                <LineChart data={lineChartData} options={chartOptions} plugins={chartPlugins} />
                            </div>
                            <div id="collision-risk-legend" className="collision-risk-section__legend"></div>
                        </>
                    )}
                    <div className="collision-risk-section__graph-filters">
                        {FILTER_LABELS.map((label, index) => {
                            return (
                                <Pill key={label} className="collision-risk-section__filter-pill" active={currentFilter === index} onChange={() => setCurrentFilter(index)}>
                                    {translate(label)}
                                </Pill>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div id="collision-risk-tooltip" className="collision-risk-tooltip"></div>
        </>
    );
};

export const TRANSLATIONS: string[] = [
    LOW_RISK,
    MODERATE_RISK,
    HIGH_RISK,
    LOW_RISK_LOWERCASE,
    MODERATE_RISK_LOWERCASE,
    HIGH_RISK_LOWERCASE,
    NO_DATA_AVAILABLE,
    RISK_MESSAGE_METRIC_TEMPLATE,
    RISK_MESSAGE_IMPERIAL_TEMPLATE,
    INFO_MODAL_TITLE,
    COLLISION_RISK_MODAL_TEMPLATE,
    ESTIMATED_VALUE,
    LAST_UPDATED,
    YOU_LABEL,
    TOP_PERFORMER_LABEL,
    LAST_7_DAYS,
    LAST_30_DAYS,
    LAST_3_MONTHS,
    LAST_6_MONTHS,
    LAST_12_MONTHS
];
